import {
  Stack,
  Paper,
  Group,
  Title,
  Button,
  Text,
  Grid,
  Center,
  ThemeIcon,
} from "@mantine/core"
import {
  IconExternalLink,
  IconEye,
  IconArrowUpRight,
  IconArrowDownRight,
} from "@tabler/icons-react"
import qs from "qs"
import { formatQuery } from "react-querybuilder"
import { useNavigate } from "react-router-dom"

import { LabelsExplorerResponses } from "@costory/types/endpoints/labelsExplorer"
import { Filters } from "@costory/types/filters"
import { AggBy, Currency, DatePreset } from "@costory/types/prisma-client"

import { computePresetDates } from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

import { CostBreakdownChart } from "@costory/front/pages/LabelsExplorer/CostBreakdownChart"
import { TopValuesTable } from "@costory/front/pages/LabelsExplorer/TopValues"

interface MetricCardProps {
  title: string
  value: string
  subtitle: string
  trend?: number
}

function MetricCard({ title, value, subtitle, trend }: MetricCardProps) {
  const isPositive = trend !== undefined && trend >= 0
  return (
    <Paper bg="surfaceCard" withBorder p="xs" radius="md" h="100%">
      <Stack justify="space-between" h="100%" gap="xs">
        <Text size="xs" c="dimmed">
          {title}
        </Text>
        <div>
          <Group align="flex-end" gap={4}>
            <Text size="md" fw={700} lh={1}>
              {value}
            </Text>
            {trend !== undefined && (
              <Group gap={2} c={isPositive ? "teal" : "red"}>
                {isPositive ? (
                  <IconArrowUpRight size={12} />
                ) : (
                  <IconArrowDownRight size={12} />
                )}
                <Text size="xs" fw={500}>
                  {Math.abs(trend)}%
                </Text>
              </Group>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        </div>
      </Stack>
    </Paper>
  )
}
export function LabelDetails({
  label,
}: {
  label: LabelsExplorerResponses.LabelsStatsFullBilling
}) {
  const navigate = useNavigate()

  const filters: Filters = {
    metricId: "cost",
    currency: "USD" as Currency,
    limit: 10,
    aggBy: "Day" as AggBy,
    groupBy: label.key,
    ...computePresetDates(DatePreset.LAST_MONTH),
    datePreset: DatePreset.LAST_MONTH,
    whereClause: {
      combinator: "and" as const,
      rules: [
        {
          field: label.key,
          operator: "in",
          value: label.values.map((v) => v.value),
        },
      ],
    },
  }
  const queryFormatted = qs.stringify(
    {
      ...filters,
      whereClause: encodeURIComponent(
        formatQuery(filters.whereClause, "json_without_ids"),
      ),
    },
    { encode: false },
  )

  return (
    <Paper p="sm" h="100%" style={{ overflowY: "auto" }}>
      <Group justify="space-between" mb="sm">
        <Title order={3}>{label.key}</Title>
        {label.ingested && (
          <Button
            size="sm"
            variant="light"
            leftSection={<IconExternalLink size={16} />}
            onClick={() => {
              navigate(`/explorer?${queryFormatted}`)
            }}
          >
            Preview in Explorer
          </Button>
        )}
      </Group>

      <Stack gap="sm">
        <Grid>
          <Grid.Col span={4}>
            <MetricCard
              title="Cost Allocated"
              value={
                label?.proportion_key_present !== undefined
                  ? `${(label.proportion_key_present * 100).toFixed(2)}%`
                  : "N/A"
              }
              subtitle="Past 30 days"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <MetricCard
              title="Total Cost"
              value={formatNumber({
                value: label.total_cost_key_present,
              })}
              subtitle="Past 30 days"
              trend={
                label?.cost_percentage_change != null
                  ? Number(label.cost_percentage_change.toFixed(2))
                  : undefined
              }
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <MetricCard
              title="Unique Values"
              value={label.values.length.toString()}
              subtitle="Past 30 days"
            />
          </Grid.Col>
        </Grid>
        <Paper withBorder p="sm" radius="md">
          <CostBreakdownChart label={label} />
        </Paper>

        <TopValuesTable values={label.values} />
      </Stack>
    </Paper>
  )
}

export function LabelDetailsPlaceholder() {
  return (
    <Paper p="xl" h="100%">
      <Center h="100%">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} variant="light" color="textPrimary">
            <IconEye size={32} />
          </ThemeIcon>
          <Title order={3} ta="center">
            Hover over a label to preview
          </Title>
          <Text c="dimmed" ta="center">
            Hover over any label from the list to see its detailed analysis.
          </Text>
        </Stack>
      </Center>
    </Paper>
  )
}
